// Top-bar WhatsApp account switcher. Shows which number the app is scoped to;
// "All accounts" merges every number's conversations in the inbox. Hidden when
// the tenant has a single number — nothing to switch.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../lib/i18n';
import { ALL_ACCOUNTS, numberLabel, useAccounts } from '../../lib/accounts';
import type { ConnectedNumber } from '../../lib/whatsappApi';
import { cn } from '../../lib/cn';

function Dot({ n }: { n: ConnectedNumber }) {
  const color = n.isPaused ? 'bg-amber-300' : n.isLive ? 'bg-emerald-300' : 'bg-slate-300';
  return <span className={cn('inline-block h-2 w-2 shrink-0 rounded-full', color)} aria-hidden />;
}

export default function AccountSwitcher() {
  const { t } = useI18n();
  const { numbers, selectedId, setSelectedId, defaultId, loading } = useAccounts();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  if (loading || numbers.length < 2) return null;

  const current = numbers.find((n) => n.id === selectedId) ?? null;
  const title = current ? numberLabel(current) : t('accounts.all');

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex max-w-[220px] items-center gap-2 rounded-full border border-line bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-700 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
        title={t('accounts.switchTitle')}
      >
        <span aria-hidden>📱</span>
        <span className="truncate">{title}</span>
        <span aria-hidden className="text-xs opacity-60">▾</span>
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute end-0 top-full z-30 mt-2 w-72 overflow-hidden rounded-card border border-line bg-white text-ink shadow-xl dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
        >
          <div className="px-3 pb-1 pt-2.5 text-[11px] font-bold uppercase tracking-wide text-ink-3 dark:text-slate-400">
            {t('accounts.pick')}
          </div>
          <button
            type="button"
            role="option"
            aria-selected={selectedId === ALL_ACCOUNTS}
            onClick={() => { setSelectedId(ALL_ACCOUNTS); setOpen(false); }}
            className={cn(
              'flex w-full items-center gap-2 px-3 py-2 text-start text-xs hover:bg-slate-50 dark:hover:bg-slate-800',
              selectedId === ALL_ACCOUNTS && 'bg-brand-50 font-bold text-brand dark:bg-slate-800 dark:text-brand-tint',
            )}
          >
            <span aria-hidden>🗂️</span>
            <span className="flex-1">{t('accounts.all')}</span>
            <span className="text-[11px] text-ink-3 dark:text-slate-400">{numbers.length}</span>
          </button>
          {numbers.map((n) => (
            <button
              key={n.id}
              type="button"
              role="option"
              aria-selected={selectedId === n.id}
              onClick={() => { setSelectedId(n.id); setOpen(false); }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-start text-xs hover:bg-slate-50 dark:hover:bg-slate-800',
                selectedId === n.id && 'bg-brand-50 font-bold text-brand dark:bg-slate-800 dark:text-brand-tint',
              )}
            >
              <Dot n={n} />
              <span className="min-w-0 flex-1">
                <span className="block truncate">{numberLabel(n)}</span>
                {n.label && n.verifiedPhoneE164 && (
                  <span className="block truncate text-[11px] font-normal text-ink-3 dark:text-slate-400" dir="ltr">
                    {n.verifiedPhoneE164}
                  </span>
                )}
              </span>
              {n.coexistence && <span className="text-[10px] font-bold text-sky-700" title={t('accounts.coexBadge')}>📱</span>}
              {n.id === defaultId && <span className="text-[10px] font-bold text-brand">{t('accounts.default')}</span>}
            </button>
          ))}
          <Link
            to="/connect"
            onClick={() => setOpen(false)}
            className="block border-t border-line px-3 py-2 text-xs font-bold text-brand hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800"
          >
            + {t('accounts.manage')}
          </Link>
        </div>
      )}
    </div>
  );
}
